import { isDefined } from '@dxb-web-ui-framework/common/utils/Helper';
import { Box, Button, Container, Divider, Drawer, makeStyles, Typography } from '@dxb-web-ui-framework/design-system';
import { Close as CloseIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import clsx from 'clsx';
import React, { FC, useCallback } from 'react';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';

export interface DrawerAction {
  dataTestId: string;
  icon: React.ReactNode;
  disabled: boolean;
  fullWidth?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'large';
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
  isIconButton: boolean;
  onClick: () => void;
  title: string;
}

export interface GenericDrawerProps {
  anchor?: 'left' | 'top' | 'right' | 'bottom';
  title?: string | React.ReactNode;
  desktopWidth?: string;
  isOpen: boolean;
  disableMobileDrawerHeight?: boolean;
  onToggleDrawer: (data?: any) => void;
  actions?: DrawerAction[];
  stickDrawerTitleToLeft?: boolean;
  isConfirmationResponsiveDialog?: boolean;
  disableTitleDrawerPaddings?: boolean;
  DownloadTransactionDetailsComponent?: React.ComponentType<any>;
  titleTypographyVariant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'button'
    | 'overline';
  customDrawerBodyClass?: string;
}

export interface ExposedActions {
  onToggleDrawer: () => void;
}

const useStyles = makeStyles(({ spacing }) => ({
  drawer: ({
    isMobile,
    desktopWidth,
    isConfirmationResponsiveDialog,
  }: {
    isMobile: boolean;
    desktopWidth?: string;
    isConfirmationResponsiveDialog?: boolean;
  }) => ({
    '& .MuiDrawer-paperAnchorBottom': {
      height: isConfirmationResponsiveDialog ? `unset` : `${isMobile ? 100 : 70}%`,
      overflow: 'hidden',
      maxHeight: '100%',
    },
    '& .MuiDrawer-paperAnchorRight': {
      width: `${isMobile ? '100vw' : desktopWidth}`,
      height: '100%',
    },
  }),
  title: {
    paddingRight: spacing(4),
    paddingTop: spacing(2),
    paddingBottom: spacing(2),
  },
  disableTitlePaddings: {
    paddingRight: spacing(0),
    paddingTop: spacing(0),
    paddingBottom: spacing(0),
  },
  drawerBody: {
    overflow: 'auto',
    height: '100%',
  },
  drawerHeight: {
    height: '90%',
  },
  drawerHeaderContainer: {
    padding: `${spacing(0)}px ${spacing(3)}px`,
  },
}));

const GenericDrawer: FC<GenericDrawerProps> = ({
  anchor,
  isOpen,
  desktopWidth = '500px',
  disableMobileDrawerHeight,
  onToggleDrawer,
  title,
  actions,
  stickDrawerTitleToLeft,
  titleTypographyVariant,
  disableTitleDrawerPaddings,
  isConfirmationResponsiveDialog,
  customDrawerBodyClass,
  DownloadTransactionDetailsComponent,
  children,
}) => {
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const styles = useStyles({ isMobile, desktopWidth, isConfirmationResponsiveDialog });

  const defaultDrawerTitleTypographyVariant = isMobile ? 'subtitle1' : 'h6';
  const drawersTitleTypographyVariant = isDefined(titleTypographyVariant)
    ? titleTypographyVariant
    : defaultDrawerTitleTypographyVariant;

  const renderActions = useCallback(() => {
    if (actions) {
      return actions?.map((action, index) => (
        <Button
          key={index}
          data-testid={action.dataTestId}
          disabled={action.disabled}
          fullWidth={action.fullWidth}
          variant={action.variant}
          size={action.size}
          color={action.color}
          isIconButton={action.isIconButton}
          onClick={action.onClick}
          title={action.title}>
          {action.icon}
        </Button>
      ));
    }
    return null;
  }, [actions]);

  return (
    <Drawer data-testid='drawer' anchor={anchor} open={isOpen} onClose={onToggleDrawer} className={styles.drawer}>
      <Box
        role={'region'}
        className={clsx({
          [styles.drawerHeight]: !disableMobileDrawerHeight && isMobile,
        })}>
        <Container maxWidth={false} disableGutters className={styles.drawerHeaderContainer}>
          <Box
            display='flex'
            paddingTop={anchor !== 'bottom' ? 4 : 3}
            paddingBottom={anchor !== 'bottom' ? 4 : 3}
            justifyContent={title ? 'space-between' : 'flex-end'}
            alignItems='center'>
            {title && (
              <Box ml={Boolean(stickDrawerTitleToLeft) ? 0 : 7}>
                {typeof title === 'string' ? (
                  <Typography
                    variant={drawersTitleTypographyVariant}
                    component='h3'
                    className={clsx({
                      [styles.title]: anchor !== 'bottom',
                      [styles.disableTitlePaddings]: disableTitleDrawerPaddings === true,
                    })}>
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </Box>
            )}
            <Box display='flex'>
              {renderActions()}
              {DownloadTransactionDetailsComponent && <DownloadTransactionDetailsComponent />}
              <Box pl={1}>
                <Button
                  fullWidth
                  variant='text'
                  size='large'
                  color='secondary'
                  title='Close'
                  data-testid='close-drawer-btn'
                  isIconButton={true}
                  onClick={onToggleDrawer}>
                  <CloseIcon color='secondary' />
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
        {title && <Divider />}
      </Box>
      <Box className={`${styles.drawerBody} ${customDrawerBodyClass}`}>{children}</Box>
    </Drawer>
  );
};

export { GenericDrawer };
